/**
 * The form assistant's two jobs on the client (068):
 *
 *  - `formSnapshot` — the working copy as the model reads it. Compact and
 *    stable: sections with their questions, the rules as stored, the submit
 *    label. Keys, section ids and rule ids are the vocabulary the model's
 *    proposal has to speak, so they are what it is given.
 *  - `applyProposal` — the model's proposed edits, made to a COPY of the
 *    draft through the same primitives the builder uses (`newField`,
 *    `deriveKeyIn`, `freeSectionId`…). Every key and id is re-checked against
 *    the draft as it stands at the click, and an edit that no longer fits is
 *    skipped and said out loud rather than guessed at. The caller commits the
 *    result as ONE undo step; nothing is published.
 */
import type { FieldOption, FormRule, RuleAction } from '../../shared/types';
import type { FormDraft } from './FormWorkspace';
import type { ProposedChange, ProposedFieldSpec, ProposedRule } from '../../api/forms';
import {
  BField, BInputType, BSection, OPTION_TYPES, PRESENTATION, asksNothing, countKeysOf, defaultStyle, deriveKeyIn,
  freeSectionId, isStructural, keyProblem, newField, ruleText,
} from './builderModel';

// ---------------------------------------------------------------------------
// What the model reads
// ---------------------------------------------------------------------------

const optionsOf = (list: FieldOption[] | undefined) =>
  (list || []).map((o) => (o.value === o.text ? o.text : { value: o.value, text: o.text }));

function fieldSnapshot(f: BField, cardTitle: (id: string) => string | undefined) {
  const presentational = asksNothing(f);
  return {
    key: f.key,
    label: f.type === 'paragraph' ? (f.paragraph?.text || '').slice(0, 300) : f.label,
    type: f.type,
    ...(!presentational && f.req === 'whenShown' ? { required: true } : {}),
    ...(f.vis === 'hidden' ? { hidden: true } : {}),
    ...(f.lock === 'locked' ? { locked: true } : {}),
    ...(f.options?.length ? { options: optionsOf(f.options) } : {}),
    ...(f.optionsPreset ? { optionsPreset: f.optionsPreset } : {}),
    ...(f.allowOther ? { allowOther: true } : {}),
    ...(f.placeholder ? { placeholder: f.placeholder } : {}),
    ...(f.infoText ? { infoText: f.infoText } : {}),
    ...(f.group ? { card: cardTitle(f.group) || f.group } : {}),
    ...(f.scope && f.scope !== 'order' ? { repeatsIn: f.scope } : {}),
    ...(f.type === 'matrix' && f.matrix
      ? { matrixRows: optionsOf(f.matrix.rows), matrixColumns: optionsOf(f.matrix.columns) } : {}),
  };
}

/** The working copy as the assistant reads it. */
export function formSnapshot(draft: FormDraft) {
  return {
    submitLabel: draft.submitLabel || '(the default, "Submit")',
    sections: draft.sections.map((s) => {
      const cardTitle = (id: string) => s.cards.find((c) => c.id === id)?.title;
      return {
        id: s.id,
        title: s.title,
        ...(s.desc ? { desc: s.desc } : {}),
        ...(s.type !== 'fields' ? { type: s.type } : {}),
        ...(s.pageBreak ? { startsNewPage: true } : {}),
        ...(s.hidden ? { hidden: true } : {}),
        ...(s.cards.length ? { cards: s.cards.map((c) => ({ id: c.id, title: c.title, ...(c.hidden ? { hidden: true } : {}) })) } : {}),
        fields: draft.fields.filter((f) => f.section === s.id && !isStructural(f)).map((f) => fieldSnapshot(f, cardTitle)),
      };
    }),
    rules: draft.rules,
  };
}

// ---------------------------------------------------------------------------
// Making the edits
// ---------------------------------------------------------------------------

export interface ApplyResult {
  draft: FormDraft;
  /** edits made */
  applied: number;
  /** a sentence per edit that could not be made, and why */
  skipped: string[];
}

const opts = (list: string[] | undefined): FieldOption[] => {
  const seen = new Set<string>();
  return (list || []).map((o) => o.trim()).filter(Boolean)
    .filter((o) => (seen.has(o.toLowerCase()) ? false : (seen.add(o.toLowerCase()), true)))
    .map((o) => ({ value: o, text: o }));
};

/** New option texts, keeping the stored VALUE of any option whose text survives — answers and rules match on it. */
const reuseValues = (next: string[], prev: FieldOption[]): FieldOption[] =>
  opts(next).map((o) => prev.find((p) => p.text.toLowerCase() === o.text.toLowerCase()) || o);

const clampStr = (n: number | undefined, min: number, max: number, d: number): string =>
  String(Math.min(max, Math.max(min, Number.isFinite(n) ? Math.round(n as number) : d)));

function buildField(spec: ProposedFieldSpec, fields: BField[], draft: FormDraft): BField | string {
  const type = spec.type as BInputType;
  const f = newField(type, fields, countKeysOf(draft.widgets));
  f.label = spec.label;
  if (spec.key && !keyProblem(spec.key, fields, '', draft.widgets)) f.key = spec.key;
  else f.key = deriveKeyIn(spec.label, fields, countKeysOf(draft.widgets));
  if (!PRESENTATION.has(type)) f.req = spec.required ? 'whenShown' : 'never';
  if (spec.placeholder) f.placeholder = spec.placeholder;
  if (spec.infoText) f.infoText = spec.infoText;
  if (OPTION_TYPES.includes(type)) {
    const o = opts(spec.options);
    if (!o.length) return `“${spec.label}” was not added: a ${type} needs its choices.`;
    f.options = spec.allowOther ? o.filter((x) => x.text.toLowerCase() !== 'other') : o;
  }
  if (spec.allowOther && ['select', 'radio', 'multiselect'].includes(type)) f.allowOther = true;
  if (type === 'rating') f.ratingMax = clampStr(spec.ratingMax, 3, 10, 5);
  if (type === 'scale') {
    f.scale = {
      min: spec.scaleMin === 0 ? '0' : '1',
      max: clampStr(spec.scaleMax, 2, 10, 5),
      minLabel: spec.scaleMinLabel || '',
      maxLabel: spec.scaleMaxLabel || '',
    };
  }
  if (type === 'matrix') {
    const rows = opts(spec.matrixRows), columns = opts(spec.matrixColumns);
    if (!rows.length || !columns.length) return `“${spec.label}” was not added: a matrix needs rows and columns.`;
    f.matrix = { rows, columns, multi: false };
  }
  if (type === 'heading') f.heading = { level: spec.level === 2 ? '2' : spec.level === 3 ? '3' : '1' };
  if (type === 'paragraph') {
    f.paragraph = { text: spec.text || spec.label };
    f.label = 'Paragraph';
  }
  return f;
}

/** Put `f` after `afterKey` (and its row) when that is in the section's own body, else at the end of the body. */
function placeField(fields: BField[], f: BField, sectionId: string, afterKey?: string) {
  f.section = sectionId;
  f.group = '';
  f.scope = 'order';
  f.row = null;
  const anchor = afterKey
    ? fields.find((x) => x.key === afterKey && x.section === sectionId && !isStructural(x))
    : undefined;
  if (anchor) {
    f.group = anchor.group || '';
    f.scope = anchor.scope || 'order';
    if (f.scope !== 'order') f.flow = 'complex';
    let i = fields.indexOf(anchor);
    // Past the rest of its row, so the new field does not split one.
    while (anchor.row && i + 1 < fields.length && fields[i + 1].row === anchor.row) i += 1;
    fields.splice(i + 1, 0, f);
    return;
  }
  let last = -1;
  fields.forEach((x, i) => { if (x.section === sectionId && !x.group && (x.scope || 'order') === 'order') last = i; });
  if (last >= 0) fields.splice(last + 1, 0, f);
  else {
    // An empty section: after everything in the sections before it.
    const before = fields.reduce((at, x, i) => (x.section === sectionId ? at : i), -1);
    fields.splice(before + 1, 0, f);
  }
}

function freshRuleId(rules: FormRule[]): string {
  const used = new Set(rules.map((r) => r.id));
  let n = rules.length + 1;
  while (used.has(`r${n}`)) n += 1;
  return `r${n}`;
}

/** A rule whose every field, section, card and page reference exists — or why not. */
function checkedRule(rule: ProposedRule, id: string, d: FormDraft): FormRule | string {
  const questions = new Set(d.fields.filter((f) => !isStructural(f) && !asksNothing(f)).map((f) => f.key));
  const targets = new Set([
    ...d.fields.filter((f) => !isStructural(f)).map((f) => f.key),
    ...d.sections.map((s) => s.id),
    ...d.sections.flatMap((s) => s.cards.map((c) => c.id)),
  ]);
  const pages = new Set(d.sections.filter((s, i) => i === 0 || s.pageBreak).map((s) => s.id));
  const missing = [
    ...rule.when.map((w) => w.field).filter((k) => !questions.has(k)),
    ...rule.actions.flatMap((a) => (a.type === 'goto' ? [] : a.targets.filter((t) => !targets.has(t)))),
  ];
  if (missing.length) return `The rule “${rule.name}” was not added: nothing on the form is called ${[...new Set(missing)].map((k) => `“${k}”`).join(', ')}.`;
  const badPage = rule.actions.find((a) => a.type === 'goto' && !pages.has(a.page));
  if (badPage) return `The rule “${rule.name}” was not added: it jumps to a section that does not start a page.`;
  return {
    id, kind: 'logic', name: rule.name, trigger: rule.trigger, match: rule.match,
    when: rule.trigger === 'always' ? [] : rule.when.map((w) => ({ field: w.field, op: w.op, value: w.value ?? '' })),
    actions: rule.actions as RuleAction[],
  };
}

/**
 * Make the proposed edits to a copy of the draft. Structure first (sections,
 * then fields, in the order proposed), rules last — so a rule may name a field
 * the same proposal adds, whatever order the model listed them in.
 */
export function applyProposal(draft: FormDraft, changes: ProposedChange[]): ApplyResult {
  const d: FormDraft = {
    ...draft,
    sections: draft.sections.map((s) => ({ ...s })),
    fields: draft.fields.map((f) => ({ ...f })),
    rules: [...draft.rules],
  };
  const skipped: string[] = [];
  let applied = 0;
  /** A section id the model chose for a new section → the id it got. */
  const sectionIds = new Map<string, string>();
  const sectionOf = (id: string) => d.sections.find((s) => s.id === (sectionIds.get(id) ?? id));
  const fieldOf = (key: string) => d.fields.find((f) => f.key === key && !isStructural(f));

  const structural = changes.filter((c) => !['add_rule', 'update_rule', 'remove_rule'].includes(c.op));
  const rules = changes.filter((c) => ['add_rule', 'update_rule', 'remove_rule'].includes(c.op));

  for (const c of [...structural, ...rules]) {
    switch (c.op) {
      case 'add_section': {
        const id = freeSectionId(c.id.replace(/[^a-zA-Z0-9_-]/g, '') || 'section', d.sections);
        sectionIds.set(c.id, id);
        const sec: BSection = {
          id, title: c.title, type: 'fields', showFlag: '', hideWith: '', cards: [], style: defaultStyle(),
          ...(c.desc ? { desc: c.desc } : {}),
        };
        const after = c.after ? d.sections.findIndex((s) => s.id === (sectionIds.get(c.after!) ?? c.after)) : -1;
        const at = after >= 0 ? after + 1 : d.sections.length;
        if (c.newPage && at > 0) sec.pageBreak = true;
        d.sections.splice(at, 0, sec);
        applied++;
        break;
      }
      case 'update_section': {
        const s = sectionOf(c.id);
        if (!s) { skipped.push(`A section “${c.id}” is not on the form, so it was not changed.`); break; }
        if (c.title !== undefined) s.title = c.title;
        if (c.desc !== undefined) s.desc = c.desc || undefined;
        if (c.newPage !== undefined && d.sections.indexOf(s) > 0) s.pageBreak = c.newPage || undefined;
        applied++;
        break;
      }
      case 'add_field': {
        const s = sectionOf(c.section);
        if (!s) { skipped.push(`“${c.field.label}” was not added: there is no section “${c.section}”.`); break; }
        if (s.type !== 'fields') { skipped.push(`“${c.field.label}” was not added: “${s.title}” is a repeating widget — add it there by hand.`); break; }
        const built = buildField(c.field, d.fields, d);
        if (typeof built === 'string') { skipped.push(built); break; }
        placeField(d.fields, built, s.id, c.after);
        applied++;
        break;
      }
      case 'update_field': {
        const f = fieldOf(c.key);
        if (!f) { skipped.push(`No field is called “${c.key}”, so it was not changed.`); break; }
        if (c.label !== undefined) {
          if (f.type === 'paragraph') f.paragraph = { text: c.label };
          else f.label = c.label;
        }
        if (c.required !== undefined && !asksNothing(f)) f.req = c.required ? 'whenShown' : 'never';
        if (c.hidden !== undefined) f.vis = c.hidden ? 'hidden' : 'shown';
        if (c.placeholder !== undefined) f.placeholder = c.placeholder;
        if (c.infoText !== undefined) f.infoText = c.infoText;
        if (c.options !== undefined && OPTION_TYPES.includes(f.type as BInputType)) {
          const next = reuseValues(c.options, f.options || []);
          if (next.length) { f.options = next; f.optionsPreset = ''; }
        }
        if (c.allowOther !== undefined && ['select', 'radio', 'multiselect'].includes(f.type)) f.allowOther = c.allowOther;
        applied++;
        break;
      }
      case 'remove_field': {
        const f = fieldOf(c.key);
        if (!f) { skipped.push(`No field is called “${c.key}”, so nothing was removed.`); break; }
        d.fields = d.fields.filter((x) => x !== f);
        const named = d.rules.filter((r) => JSON.stringify(r).includes(`"${c.key}"`));
        if (named.length) {
          skipped.push(`“${f.label}” was removed, but ${named.length === 1 ? 'a rule still names it' : `${named.length} rules still name it`} — check the Logic tab.`);
        }
        applied++;
        break;
      }
      case 'set_submit_label':
        d.submitLabel = c.label;
        applied++;
        break;
      case 'add_rule': {
        const r = checkedRule(c.rule, freshRuleId(d.rules), d);
        if (typeof r === 'string') { skipped.push(r); break; }
        d.rules = [...d.rules, r];
        applied++;
        break;
      }
      case 'update_rule': {
        const i = d.rules.findIndex((x) => x.id === c.id);
        if (i < 0) { skipped.push(`There is no rule “${c.id}”, so it was not changed.`); break; }
        const r = checkedRule(c.rule, c.id, d);
        if (typeof r === 'string') { skipped.push(r); break; }
        d.rules = d.rules.map((x, j) => (j === i ? r : x));
        applied++;
        break;
      }
      case 'remove_rule': {
        if (!d.rules.some((x) => x.id === c.id)) { skipped.push(`There is no rule “${c.id}” to remove.`); break; }
        d.rules = d.rules.filter((x) => x.id !== c.id);
        applied++;
        break;
      }
      default:
        break;
    }
  }
  return { draft: d, applied, skipped };
}

// ---------------------------------------------------------------------------
// Saying what an edit does
// ---------------------------------------------------------------------------

const TYPE_WORD: Record<string, string> = {
  text: 'short text', email: 'email', tel: 'phone', date: 'date', time: 'time', datetime: 'date and time',
  number: 'number', url: 'website', textarea: 'long text', select: 'dropdown', radio: 'multiple choice',
  multiselect: 'checkboxes', checkbox: 'checkbox', rating: 'rating', scale: 'linear scale', matrix: 'matrix',
  ranking: 'ranking', file: 'file upload', name: 'name', address: 'address', signature: 'signature',
  heading: 'heading', paragraph: 'paragraph', divider: 'divider',
};

/** One proposed edit as a sentence, in the draft's own words. */
export function describeChange(c: ProposedChange, draft: FormDraft, proposed: ProposedChange[] = []): string {
  const newSections = proposed.filter((x): x is Extract<ProposedChange, { op: 'add_section' }> => x.op === 'add_section');
  const secName = (id: string) =>
    draft.sections.find((s) => s.id === id)?.title || newSections.find((s) => s.id === id)?.title || id;
  const newFields = proposed.filter((x): x is Extract<ProposedChange, { op: 'add_field' }> => x.op === 'add_field');
  const lbl = (k: string) =>
    draft.fields.find((f) => f.key === k)?.label || newFields.find((x) => x.field.key === k)?.field.label
      || draft.sections.find((s) => s.id === k)?.title || k;
  const q = (s: string) => `“${s}”`;
  switch (c.op) {
    case 'add_field':
      return `Add ${q(c.field.type === 'paragraph' ? (c.field.text || c.field.label).slice(0, 60) : c.field.label)} (${TYPE_WORD[c.field.type] || c.field.type}${c.field.required ? ', required' : ''}${c.field.options?.length ? `: ${c.field.options.join(' / ')}` : ''}) to ${q(secName(c.section))}${c.after ? `, after ${q(lbl(c.after))}` : ''}`;
    case 'update_field': {
      const parts: string[] = [];
      if (c.label !== undefined) parts.push(`rename it ${q(c.label)}`);
      if (c.required !== undefined) parts.push(c.required ? 'make it required' : 'make it optional');
      if (c.hidden !== undefined) parts.push(c.hidden ? 'hide it' : 'show it');
      if (c.placeholder !== undefined) parts.push(c.placeholder ? `set its placeholder to ${q(c.placeholder)}` : 'clear its placeholder');
      if (c.infoText !== undefined) parts.push(c.infoText ? `set its help text to ${q(c.infoText)}` : 'clear its help text');
      if (c.options !== undefined) parts.push(`set its choices to ${c.options.join(' / ')}`);
      if (c.allowOther !== undefined) parts.push(c.allowOther ? 'offer “Other”' : 'stop offering “Other”');
      return `${q(lbl(c.key))}: ${parts.join(', ')}`;
    }
    case 'remove_field':
      return `Remove ${q(lbl(c.key))}`;
    case 'add_section':
      return `Add the section ${q(c.title)}${c.newPage ? ' on a new page' : ''}${c.after ? ` after ${q(secName(c.after))}` : ''}`;
    case 'update_section': {
      const parts: string[] = [];
      if (c.title !== undefined) parts.push(`rename it ${q(c.title)}`);
      if (c.desc !== undefined) parts.push(c.desc ? 'change its description' : 'clear its description');
      if (c.newPage !== undefined) parts.push(c.newPage ? 'start a new page there' : 'stop starting a new page there');
      return `Section ${q(secName(c.id))}: ${parts.join(', ')}`;
    }
    case 'add_rule':
    case 'update_rule': {
      const sentence = ruleText(
        { id: '', kind: 'logic', ...c.rule, actions: c.rule.actions as RuleAction[] }, lbl, draft.fields, (id) => q(secName(id)),
      );
      return c.op === 'add_rule'
        ? `New rule ${q(c.rule.name)}: ${sentence}`
        : `Change the rule ${q(draft.rules.find((r) => r.id === c.id)?.name || c.id)}: ${sentence}`;
    }
    case 'remove_rule':
      return `Remove the rule ${q(draft.rules.find((r) => r.id === c.id)?.name || c.id)}`;
    case 'set_submit_label':
      return c.label ? `Label the submit button ${q(c.label)}` : 'Put the submit button back to “Submit”';
    default:
      return 'An edit this version cannot show';
  }
}
